/**
 * Việc ưu tiên — thuần hàm trên dữ liệu đã tải. Chỉ nêu việc CÓ THỂ LÀM NGAY trong app (không khuyên đầu tư),
 * mỗi việc có đường dẫn tới nơi xử lý. Cảnh báo số dư/thẻ đã có ở khung Cảnh báo nên không lặp lại ở đây.
 */
import { hasInterestRate } from '../../../finance';
type Doc = Record<string, any>;
const num = (v: unknown) => Number(v) || 0;
const toDate = (v: any): Date | null => { if (!v) return null; const d = v.toDate ? v.toDate() : new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
const money = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

export interface PriorityAction { id: string; level: 'bad' | 'warn' | 'info'; title: string; detail: string; to: string; cta: string }

/**
 * Nhóm khoản chi giống hệt nhau (cùng ngày, số tiền, danh mục, ví) từ 2 bản ghi trở lên — có thể là ghi trùng.
 * Nhóm mà MỌI bản ghi đều đã được đánh dấu `notDuplicate` (người dùng xác nhận "không phải trùng") thì bỏ qua;
 * thêm một bản ghi mới giống hệt vào nhóm đó thì nhóm hiện lại.
 */
export const likelyDuplicateExpenses = (expenses: Doc[]): Doc[][] => {
  const groups = new Map<string, Doc[]>();
  for (const e of expenses) {
    if (e.autoGenerated || e.relatedTransferId) continue;
    const d = toDate(e.transactionDate); if (!d || !(num(e.amount) > 0)) continue;
    const key = [d.getFullYear(), d.getMonth(), d.getDate(), num(e.amount), e.category ?? e.categoryName ?? '', e.walletId ?? ''].join('|');
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.values()].filter(g => g.length >= 2 && !g.every(e => e.notDuplicate));
};

/** Khoản nghi trùng trong 45 ngày gần đây (cùng cửa sổ với cảnh báo ở Tổng quan). */
export const suspectedDuplicates = (expenses: Doc[], now: Date = new Date()): Doc[][] => {
  const since = now.getTime() - 45 * 86_400_000;
  return likelyDuplicateExpenses(expenses.filter(e => (toDate(e.transactionDate)?.getTime() ?? 0) >= since));
};

/**
 * Trong một nhóm nghi trùng, bản nên xoá: ưu tiên bản KHÔNG gắn với khoản dự kiến nào (`linkedIds` = id giao dịch đã được
 * khoản dự kiến xác nhận trỏ tới, hoặc có `plannedId`); nếu không phân biệt được thì giữ bản tạo sớm nhất.
 * Luôn giữ lại ít nhất một bản. Chỉ là gợi ý — người dùng tự xoá.
 */
export const suggestedExtras = (group: Doc[], linkedIds: Set<string>): Set<string> => {
  const linked = (e: Doc) => !!e.plannedId || linkedIds.has(e.id);
  const created = (e: Doc) => toDate(e.createdAt)?.getTime() ?? 0;
  const keep = group.filter(linked).length > 0
    ? group.filter(linked)
    : [[...group].sort((a, b) => created(a) - created(b))[0]];
  const keepIds = new Set(keep.map(e => e.id));
  return new Set(group.filter(e => !keepIds.has(e.id)).map(e => e.id));
};

export interface ActionInput {
  now: Date; sources: Doc[]; debts: Doc[]; expenses: Doc[]; budgetsSet: boolean;
}

export const priorityActions = (i: ActionInput): PriorityAction[] => {
  const out: PriorityAction[] = [];
  const day = i.now.getDate();
  const ym = `${i.now.getFullYear()}-${String(i.now.getMonth() + 1).padStart(2, '0')}`;

  // Ghi trùng trong 45 ngày gần đây
  const dups = suspectedDuplicates(i.expenses, i.now);
  if (dups.length) {
    const extra = dups.reduce((s, g) => s + num(g[0].amount) * (g.length - 1), 0);
    out.push({
      id: `dup:${dups.map(g => g[0].id).sort().join(',')}`, level: 'warn', title: `${dups.length} khoản chi có thể bị ghi trùng`,
      detail: `Cùng ngày, số tiền, danh mục và ví (${dups.slice(0, 2).map(g => `${g[0].category ?? g[0].categoryName ?? 'Chi'} ${money(num(g[0].amount))}`).join('; ')}${dups.length > 2 ? '…' : ''}). Nếu đúng là trùng, xoá bản thừa để khớp ${money(extra)}.`,
      to: '/v2/giao-dich?trung=1', cta: 'Xem khoản nghi trùng'
    });
  }

  // Thiếu lãi suất → các con số lãi/lộ trình trả nợ chỉ là giả định
  const cardsNoRate = i.sources.filter(s => s.type === 'credit' && num(s.usedCredit) > 0 && !hasInterestRate(s.interestRate));
  const loansNoRate = i.debts.filter(d => d.type !== 'credit' && (d.status === 'active' || d.status === 'overdue') && num(d.remainingBalance) > 0 && !hasInterestRate(d.interestRate));
  if (cardsNoRate.length + loansNoRate.length > 0) {
    out.push({
      id: `rates:${ym}`, level: 'info', title: `${cardsNoRate.length + loansNoRate.length} khoản nợ chưa có lãi suất`,
      detail: 'Chưa có lãi suất thì app chưa tính được tiền lãi mỗi tháng và xếp thứ tự trả nợ. Vay không lãi thì nhập 0.',
      to: '/v2/tai-san', cta: 'Nhập lãi suất'
    });
  }

  // Khoản vay sắp đến hạn tất toán mà vẫn còn dư nợ: cần kế hoạch tất toán hoặc gia hạn từ sớm
  for (const d of i.debts) {
    if (d.type === 'credit' || (d.status !== 'active' && d.status !== 'overdue') || !(num(d.remainingBalance) > 0)) continue;
    const due = toDate(d.dueDate); if (!due) continue;
    const left = Math.ceil((due.getTime() - i.now.getTime()) / 86_400_000);
    if (left > 180) continue;
    const name = d.lender || d.name || 'Khoản vay';
    out.push({
      id: `maturity:${d.id}`, level: left <= 60 ? 'bad' : 'warn',
      title: left < 0 ? `${name} đã quá hạn tất toán ${-left} ngày` : `${name} đến hạn tất toán sau ${left} ngày`,
      detail: `Dư nợ còn ${money(num(d.remainingBalance))}, hạn ${due.toLocaleDateString('vi-VN')}. Cần kế hoạch tất toán hoặc gia hạn với ngân hàng trước hạn.`,
      to: '/v2/ke-hoach', cta: 'Xem lộ trình trả nợ'
    });
  }

  // Ngân sách tháng chưa đặt
  if (!i.budgetsSet) out.push({ id: `budget:${ym}`, level: 'info', title: 'Chưa đặt ngân sách cho tháng này', detail: 'Đặt hạn mức cho vài danh mục chi lớn để được cảnh báo khi sắp vượt.', to: '/v2/ngan-sach', cta: 'Đặt ngân sách' });

  // Nhịp cuối/đầu tháng
  if (day >= 25) out.push({ id: `review:${ym}`, level: 'info', title: 'Sắp hết tháng — xem tổng kết', detail: 'So kết quả thực tế với kế hoạch trước khi sang tháng mới.', to: '/v2/bao-cao', cta: 'Mở tổng kết' });
  if (day <= 5) {
    out.push({ id: `review-prev:${ym}`, level: 'info', title: 'Tháng trước đã khép — xem tổng kết', detail: 'Xem tháng vừa rồi tốt xấu ở đâu để điều chỉnh tháng này.', to: '/v2/bao-cao', cta: 'Mở tổng kết' });
    out.push({ id: `reconcile:${ym}`, level: 'info', title: 'Đối soát số dư các ví với sao kê', detail: 'Đầu tháng là lúc dễ đối chiếu nhất; lệch nhỏ cũng nên điều chỉnh sớm.', to: '/v2/tai-san', cta: 'Mở Đối soát' });
  }
  const rank = { bad: 0, warn: 1, info: 2 } as const;
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
};
